// Project Tracker: makes the full Word (.docx) file from the tracker (Step 6 of Section 30).
// No imports and no extra packages, so it can be tested on its own.
// It only READS the tasks it is given. It never saves or changes anything.

const PROJECT_ORDER = ["Main app", "CRM", "Billing app", "Website", "All projects"];
const STATUS_ORDER = { Doing: 0, Check: 1, Pending: 2, Parked: 3, Done: 4 };

// ---------- small helpers ----------
function esc(s) {
  return String(s === undefined || s === null ? "" : s)
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF]/g, "")
    .replace(/\t/g, "    ")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function run(text, bold) {
  return "<w:r>" + (bold ? "<w:rPr><w:b/></w:rPr>" : "") + '<w:t xml:space="preserve">' + esc(text) + "</w:t></w:r>";
}

// runs: a text, or a list of [text, bold]
function para(runs, style, extraPPr) {
  const list = typeof runs === "string" ? [[runs, false]] : runs;
  const ppr = (style ? '<w:pStyle w:val="' + style + '"/>' : "") + (extraPPr || "");
  return "<w:p>" + (ppr ? "<w:pPr>" + ppr + "</w:pPr>" : "") + list.map((r) => run(r[0], r[1])).join("") + "</w:p>";
}

function dayOf(at) {
  if (typeof at === "string" && at.length >= 10) return at.slice(0, 10);
  if (at && typeof at.toDate === "function") return at.toDate().toISOString().slice(0, 10);
  return "";
}

function sectionNo(link) {
  const m = /^\s*Sec\s+(\d+)/i.exec(link || "");
  return m ? parseInt(m[1], 10) : 9999;
}

function cmpText(a, b) {
  return String(a || "").localeCompare(String(b || ""), "en", { numeric: true });
}

// ---------- the Word text (document.xml) ----------
function taskBlock(t) {
  const out = [];
  out.push(para(t.title || "(no title)", "Heading2"));
  out.push(
    para([
      ["Project: ", true], [(t.project || "-") + "     ", false],
      ["Type: ", true], [(t.type || "-") + "     ", false],
      ["Status: ", true], [(t.status === "Check" ? "Needs check" : (t.status || "-")) + "     ", false],
      ["Link: ", true], [t.link || "-", false],
    ])
  );
  if (t.targetDate) out.push(para([["Target date: ", true], [t.targetDate, false]]));
  if (t.doneWhen) out.push(para([["Done when: ", true], [t.doneWhen, false]]));
  if (t.note) out.push(para([["Note: ", true], [t.note, false]]));
  if (t.createdBy) out.push(para([["Added by: ", true], [t.createdBy, false]]));
  const lines = String(t.details || "").split(/\r?\n/).filter((l) => l.trim() !== "");
  if (lines.length) {
    out.push(para("Details", "Heading3"));
    lines.forEach((l) => {
      if (l.startsWith("- ")) out.push(para("\u2022 " + l.slice(2), "", '<w:ind w:left="360" w:hanging="220"/>'));
      else out.push(para(l));
    });
  }
  const hist = Array.isArray(t.history) ? t.history : [];
  if (hist.length) {
    out.push(para("History", "Heading3"));
    hist.forEach((h) => out.push(para((dayOf(h && h.at) ? dayOf(h.at) + " \u2014 " : "") + ((h && h.text) || ""), "", '<w:ind w:left="360"/>')));
  }
  return out;
}

export function sortOpen(tasks) {
  return tasks
    .filter((t) => t.status !== "Done")
    .sort((a, b) =>
      (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9) ||
      (PROJECT_ORDER.indexOf(a.project) + 1 || 99) - (PROJECT_ORDER.indexOf(b.project) + 1 || 99) ||
      cmpText(a.title, b.title)
    );
}

export function sortClosed(tasks) {
  return tasks
    .filter((t) => t.status === "Done")
    .sort((a, b) => sectionNo(a.link) - sectionNo(b.link) || cmpText(a.title, b.title));
}

function documentXml(tasks, rulesText, nowIso) {
  const open = sortOpen(tasks);
  const closed = sortClosed(tasks);
  const body = [];
  body.push(para("Security & Launch-Readiness Audit \u2014 from the Project Tracker", "Title"));
  body.push(
    para(
      "Made from the CRM Project Tracker on " + String(nowIso || "").slice(0, 10) + ". Open tasks: " + open.length +
      ". Done records: " + closed.length + ". This file is a copy; the tracker holds the live data."
    )
  );
  body.push(para("Rules", "Heading1"));
  const rl = String(rulesText || "").split(/\r?\n/);
  if (rl.join("").trim() === "") body.push(para("(The Rules page is empty.)"));
  else rl.forEach((l) => { if (l.trim() !== "") body.push(para(l)); });
  body.push(para("Open tasks (" + open.length + ")", "Heading1"));
  if (!open.length) body.push(para("There are no open tasks."));
  open.forEach((t) => taskBlock(t).forEach((x) => body.push(x)));
  body.push(para("Done records (" + closed.length + ")", "Heading1"));
  if (!closed.length) body.push(para("There are no done records."));
  closed.forEach((t) => taskBlock(t).forEach((x) => body.push(x)));
  const sect =
    '<w:sectPr><w:pgSz w:w="11906" w:h="16838"/><w:pgMar w:top="1134" w:right="1134" w:bottom="1134" w:left="1134" w:header="567" w:footer="567" w:gutter="0"/></w:sectPr>';
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:body>' +
    body.join("") + sect + "</w:body></w:document>"
  );
}

const STYLES_XML =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">' +
  '<w:docDefaults><w:rPrDefault><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri" w:cs="Calibri" w:eastAsia="Calibri"/><w:sz w:val="22"/><w:szCs w:val="22"/></w:rPr></w:rPrDefault>' +
  '<w:pPrDefault><w:pPr><w:spacing w:after="100" w:line="264" w:lineRule="auto"/></w:pPr></w:pPrDefault></w:docDefaults>' +
  '<w:style w:type="paragraph" w:default="1" w:styleId="Normal"><w:name w:val="Normal"/><w:qFormat/></w:style>' +
  '<w:style w:type="paragraph" w:styleId="Title"><w:name w:val="Title"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/><w:qFormat/><w:pPr><w:spacing w:after="200"/></w:pPr><w:rPr><w:b/><w:sz w:val="44"/><w:szCs w:val="44"/></w:rPr></w:style>' +
  '<w:style w:type="paragraph" w:styleId="Heading1"><w:name w:val="heading 1"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/><w:qFormat/><w:pPr><w:keepNext/><w:spacing w:before="360" w:after="120"/><w:outlineLvl w:val="0"/></w:pPr><w:rPr><w:b/><w:sz w:val="32"/><w:szCs w:val="32"/></w:rPr></w:style>' +
  '<w:style w:type="paragraph" w:styleId="Heading2"><w:name w:val="heading 2"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/><w:qFormat/><w:pPr><w:keepNext/><w:spacing w:before="280" w:after="80"/><w:outlineLvl w:val="1"/></w:pPr><w:rPr><w:b/><w:sz w:val="26"/><w:szCs w:val="26"/></w:rPr></w:style>' +
  '<w:style w:type="paragraph" w:styleId="Heading3"><w:name w:val="heading 3"/><w:basedOn w:val="Normal"/><w:next w:val="Normal"/><w:qFormat/><w:pPr><w:keepNext/><w:spacing w:before="160" w:after="60"/><w:outlineLvl w:val="2"/></w:pPr><w:rPr><w:b/><w:sz w:val="23"/><w:szCs w:val="23"/></w:rPr></w:style>' +
  "</w:styles>";

const CONTENT_TYPES =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
  '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
  '<Default Extension="xml" ContentType="application/xml"/>' +
  '<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>' +
  '<Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>' +
  "</Types>";

const ROOT_RELS =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
  '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>' +
  "</Relationships>";

const DOC_RELS =
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
  '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
  '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>' +
  "</Relationships>";

// ---------- a minimal .zip writer (no compression) ----------
let CRC_TABLE = null;
function crc32(bytes) {
  if (!CRC_TABLE) {
    CRC_TABLE = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      CRC_TABLE[n] = c >>> 0;
    }
  }
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

function zipStore(files) {
  const enc = new TextEncoder();
  const DOS_DATE = ((2026 - 1980) << 9) | (10 << 5) | 9;
  const parts = [];
  const central = [];
  let offset = 0;
  files.forEach((f) => {
    const name = enc.encode(f.name);
    const data = f.data;
    const crc = crc32(data);
    const lh = new DataView(new ArrayBuffer(30));
    lh.setUint32(0, 0x04034b50, true); lh.setUint16(4, 20, true); lh.setUint16(6, 0x0800, true);
    lh.setUint16(8, 0, true); lh.setUint16(10, 0, true); lh.setUint16(12, DOS_DATE, true);
    lh.setUint32(14, crc, true); lh.setUint32(18, data.length, true); lh.setUint32(22, data.length, true);
    lh.setUint16(26, name.length, true); lh.setUint16(28, 0, true);
    parts.push(new Uint8Array(lh.buffer), name, data);
    const ch = new DataView(new ArrayBuffer(46));
    ch.setUint32(0, 0x02014b50, true); ch.setUint16(4, 20, true); ch.setUint16(6, 20, true); ch.setUint16(8, 0x0800, true);
    ch.setUint16(10, 0, true); ch.setUint16(12, 0, true); ch.setUint16(14, DOS_DATE, true);
    ch.setUint32(16, crc, true); ch.setUint32(20, data.length, true); ch.setUint32(24, data.length, true);
    ch.setUint16(28, name.length, true); ch.setUint16(30, 0, true); ch.setUint16(32, 0, true);
    ch.setUint16(34, 0, true); ch.setUint16(36, 0, true); ch.setUint32(38, 0, true); ch.setUint32(42, offset, true);
    central.push(new Uint8Array(ch.buffer), name);
    offset += 30 + name.length + data.length;
  });
  const cdSize = central.reduce((n, p) => n + p.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true); end.setUint16(8, files.length, true); end.setUint16(10, files.length, true);
  end.setUint32(12, cdSize, true); end.setUint32(16, offset, true);
  const all = parts.concat(central, [new Uint8Array(end.buffer)]);
  const total = all.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let pos = 0;
  all.forEach((p) => { out.set(p, pos); pos += p.length; });
  return out;
}

// ---------- public ----------
// tasks: all tasks of the tracker (any status). Returns the bytes of the .docx file.
export function buildWordFile(tasks, rulesText, nowIso) {
  const enc = new TextEncoder();
  const list = Array.isArray(tasks) ? tasks : [];
  const files = [
    { name: "[Content_Types].xml", data: enc.encode(CONTENT_TYPES) },
    { name: "_rels/.rels", data: enc.encode(ROOT_RELS) },
    { name: "word/document.xml", data: enc.encode(documentXml(list, rulesText, nowIso)) },
    { name: "word/styles.xml", data: enc.encode(STYLES_XML) },
    { name: "word/_rels/document.xml.rels", data: enc.encode(DOC_RELS) },
  ];
  return zipStore(files);
}

export function downloadBytes(bytes, filename, mime) {
  const blob = new Blob([bytes], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export const WORD_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";